import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, forkJoin, from, map, switchMap } from 'rxjs';
import JSZip from 'jszip';
import * as FileSaver from 'file-saver';
import { UserService } from 'app/core/services/user.service';
import { UserDataPdfService } from 'app/core/services/user-data-pdf.service';

@Injectable({ providedIn: 'root' })
export class FullDataExportService {
  private readonly userService = inject(UserService);
  private readonly userDataPdfService = inject(UserDataPdfService);
  private readonly translateService = inject(TranslateService);

  /**
   * Builds a single ZIP with the user's personal-data PDF plus the backend
   * export (original task files + README) and triggers the browser download.
   * File and folder names follow the current UI language.
   */
  download(): Observable<void> {
    const lang = this.translateService.currentLang || 'ptBR';
    const pdfName = this.translateService.instant('pdf-export.filename');
    const zipBaseName = this.translateService.instant(
      'privacy.export-all-filename',
    );

    const pdf$ = this.userService
      .exportUserData()
      .pipe(switchMap((data) => this.userDataPdfService.generateBytes(data)));
    const zip$ = this.userService.exportUserDataFull(lang);

    return forkJoin({ pdfBytes: pdf$, zipBlob: zip$ }).pipe(
      switchMap(({ pdfBytes, zipBlob }) =>
        from(
          zipBlob.arrayBuffer().then(async (buf) => {
            const zip = await new JSZip().loadAsync(buf);
            zip.file(pdfName, pdfBytes);
            return zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
          }),
        ),
      ),
      map((finalBlob) => {
        const datePart = new Date().toISOString().slice(0, 10);
        FileSaver.saveAs(finalBlob, `${zipBaseName}-${datePart}.zip`);
      }),
    );
  }
}
